import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type ApiError } from '../api/client';
import { useAuth, type User } from '../auth/AuthContext';
import { Logo } from '../components/Logo';

type Status = 'checking' | 'invalid' | 'ready' | 'submitting';

export function AcceptInvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState<Status>('checking');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api<{ ok: true; email: string; name: string }>(`/api/auth/accept-invite/${encodeURIComponent(token)}`)
      .then((res) => {
        setEmail(res.email);
        setName(res.name);
        setStatus('ready');
      })
      .catch(() => setStatus('invalid'));
  }, [token]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (password.length < 12) { setError('Password must be at least 12 characters.'); return; }
    setStatus('submitting');
    try {
      await api<User>('/api/auth/accept-invite', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setStatus('ready');
      const e = err as ApiError;
      setError(e.message === 'invalid_or_expired' ? 'This invite link has expired or already been used.' : (e.message ?? 'Failed.'));
    }
  }

  return (
    <div className="auth-screen">
      <form className="auth-card" onSubmit={(e) => void onSubmit(e)} noValidate>
        <div className="auth-brand"><Logo size={48} ariaLabel="VietSurvive" /><span>VietSurvive</span></div>

        {status === 'checking' && <p style={{ color: 'var(--text-muted)' }}>Checking your invite…</p>}

        {status === 'invalid' && (
          <>
            <h1 className="auth-title">Invite link expired</h1>
            <div className="form-error" role="alert">This link is invalid or has already been used. Ask your admin to send a new one.</div>
            <Link to="/login" className="btn-secondary btn-block" style={{ textAlign: 'center' }}>Back to sign in</Link>
          </>
        )}

        {(status === 'ready' || status === 'submitting') && (
          <>
            <h1 className="auth-title">Welcome, {name}</h1>
            <p className="auth-lede">Pick a password to finish setting up <strong>{email}</strong>.</p>
            <div className="form-group">
              <label htmlFor="pw">Password</label>
              <input id="pw" type="password" required autoComplete="new-password" minLength={12} value={password} onChange={(e) => setPassword(e.target.value)} disabled={status === 'submitting'} />
            </div>
            {error && <div className="form-error" role="alert">{error}</div>}
            <button type="submit" className="btn-primary btn-block" disabled={status === 'submitting'}>
              {status === 'submitting' ? 'Setting up…' : 'Create account'}
            </button>
          </>
        )}
      </form>
      <p className="auth-copyright">© {new Date().getFullYear()} VietSurvive</p>
    </div>
  );
}
